import { useCallback, useState, MutableRefObject } from 'react';
import { useDiagramStore } from '../stores/diagramStore';
import { snapshotFromStore } from '../stores/undoStore';
import type { Node, Edge } from '../types';

export function useWaypointDrag(edges: Edge[], nodes: Node[], rKeyHeld: MutableRefObject<boolean>) {
  const [waypointCandidateEdgeId, setWaypointCandidateEdgeId] = useState<string | null>(null);

  const findCandidateEdge = useCallback((waypointNodeId: string, cx: number, cy: number, waypointW: number, waypointH: number): string | null => {
    const SNAP_DIST = 24;
    const pcx = cx + waypointW / 2;
    const pcy = cy + waypointH / 2;
    for (const edge of edges) {
      if (edge.sourceNodeId === waypointNodeId || edge.targetNodeId === waypointNodeId) continue;
      const src = nodes.find(n => n.id === edge.sourceNodeId);
      const tgt = nodes.find(n => n.id === edge.targetNodeId);
      if (!src || !tgt) continue;
      const srcSock = src.sockets.find(s => s.id === edge.sourceSocketId);
      const tgtSock = tgt.sockets.find(s => s.id === edge.targetSocketId);
      if (!srcSock || !tgtSock) continue;
      const x1 = src.position.x + srcSock.position.x;
      const y1 = src.position.y + srcSock.position.y;
      const x2 = tgt.position.x + tgtSock.position.x;
      const y2 = tgt.position.y + tgtSock.position.y;
      const dx = x2 - x1; const dy = y2 - y1;
      const lenSq = dx * dx + dy * dy;
      if (lenSq === 0) continue;
      const t = Math.max(0, Math.min(1, ((pcx - x1) * dx + (pcy - y1) * dy) / lenSq));
      const nearX = x1 + t * dx; const nearY = y1 + t * dy;
      const dist = Math.sqrt((pcx - nearX) ** 2 + (pcy - nearY) ** 2);
      if (dist < SNAP_DIST) return edge.id;
    }
    return null;
  }, [edges, nodes]);

  const handleWaypointDragMove = useCallback((nodeId: string, cx: number, cy: number) => {
    const node = useDiagramStore.getState().nodes.find(n => n.id === nodeId);
    if (!node) return;
    const candidate = findCandidateEdge(nodeId, cx, cy, node.size.width, node.size.height);
    setWaypointCandidateEdgeId(candidate);
  }, [findCandidateEdge]);

  const handleWaypointDragEnd = useCallback((nodeId: string, cx: number, cy: number) => {
    setWaypointCandidateEdgeId(null);
    const st = useDiagramStore.getState();
    const node = st.nodes.find(n => n.id === nodeId);
    if (!node) return;

    const alreadyConnected = st.edges.some(e => e.sourceNodeId === nodeId || e.targetNodeId === nodeId);
    if (alreadyConnected && !rKeyHeld.current) return;

    const candidateId = findCandidateEdge(nodeId, cx, cy, node.size.width, node.size.height);
    if (!candidateId) return;
    const edge = st.edges.find(e => e.id === candidateId);
    if (!edge) return;

    const waypointIn  = node.sockets.find(s => s.type === 'input');
    const waypointOut = node.sockets.find(s => s.type === 'output');
    if (!waypointIn || !waypointOut) return;

    snapshotFromStore();
    if (alreadyConnected) {
      st.edges
        .filter(e => e.sourceNodeId === nodeId || e.targetNodeId === nodeId)
        .forEach(e => useDiagramStore.getState().removeEdge(e.id));
    }
    const st2 = useDiagramStore.getState();
    st2.removeEdge(edge.id);
    st2.addEdge({ sourceNodeId: edge.sourceNodeId, sourceSocketId: edge.sourceSocketId, targetNodeId: nodeId,            targetSocketId: waypointIn.id });
    st2.addEdge({ sourceNodeId: nodeId,            sourceSocketId: waypointOut.id,       targetNodeId: edge.targetNodeId, targetSocketId: edge.targetSocketId });
  }, [findCandidateEdge]);

  return { rKeyHeld, waypointCandidateEdgeId, findCandidateEdge, handleWaypointDragMove, handleWaypointDragEnd };
}
